import { useEffect, useState } from 'react';
import { BellRing, MicOff, Mic, Users, UserMinus } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Initiator/admin tools shared by both call modes: ring back members who haven't joined, mute individual members.
export default function CallExtras({ callId, token, isInitiator, isAdmin, members = [], myId }) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState([]);
  const [muted, setMuted] = useState({});
  const canManage = isInitiator || isAdmin;
  const hdr = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    if (!callId || callId === 'pending' || !canManage) return undefined;
    const load = () => fetch(`${API}/api/calls/${callId}/pending`, { headers: hdr }).then((r) => (r.ok ? r.json() : { pending: [] })).then((d) => setPending(d.pending || [])).catch(() => {});
    load(); const t = setInterval(load, 8000); return () => clearInterval(t);
  }, [callId, canManage]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!canManage || !callId || callId === 'pending') return null;
  const ring = (uid, name) => fetch(`${API}/api/calls/${callId}/ring/${uid}`, { method: 'POST', headers: hdr })
    .then((r) => { if (!r.ok) throw new Error('Could not ring'); toast.success(`Ringing ${name} again…`); }).catch((e) => toast.error(e.message));
  const remove = (uid, name) => {
    if (!window.confirm(`Remove ${name} from this call?`)) return;
    fetch(`${API}/api/calls/${callId}/remove/${uid}`, { method: 'POST', headers: hdr })
      .then(async (r) => { if (!r.ok) throw new Error((await r.json()).detail || 'Could not remove'); toast.success(`${name} removed from the call`); }).catch((e) => toast.error(e.message));
  };
  const mute = (uid, name, m) => fetch(`${API}/api/calls/${callId}/mute/${uid}`, { method: 'POST', headers: hdr, body: JSON.stringify({ muted: m }) })
    .then((r) => { if (!r.ok) throw new Error('Could not change mute'); setMuted((s) => ({ ...s, [uid]: m })); toast.success(`${name} ${m ? 'muted' : 'unmuted'}`); }).catch((e) => toast.error(e.message));
  const others = members.filter((m) => m.user_id && m.user_id !== myId);
  return (
    <div className="absolute top-16 right-3 z-20 text-xs">
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex items-center gap-1.5 rounded-full bg-black/60 hover:bg-black/80 text-white px-3 py-1.5" data-testid="call-extras-btn">
        <Users className="w-3.5 h-3.5" /> Members{pending.length ? <span className="ml-1 rounded-full bg-amber-500 text-black px-1.5 font-semibold" data-testid="call-pending-count">{pending.length} not joined</span> : null}
      </button>
      {open && (
        <div className="mt-1 w-64 rounded-lg bg-gray-900/95 text-white shadow-xl p-2 space-y-2" data-testid="call-extras-panel">
          {pending.length > 0 && (
            <div>
              <p className="text-gray-400 mb-1">Not joined yet</p>
              {pending.map((p) => (
                <div key={p.user_id} className="flex items-center justify-between gap-2 py-0.5" data-testid={`call-pending-${p.user_id}`}>
                  <span className="truncate">{p.name} <span className="text-gray-400">· {p.state}</span></span>
                  <button type="button" onClick={() => ring(p.user_id, p.name)} className="flex items-center gap-1 rounded bg-green-600 hover:bg-green-500 px-2 py-0.5" data-testid={`call-ring-${p.user_id}`}><BellRing className="w-3 h-3" /> Ring</button>
                </div>
              ))}
            </div>
          )}
          {others.length > 0 && (
            <div>
              <p className="text-gray-400 mb-1">In the call — mute / remove</p>
              {others.map((m) => (
                <div key={m.user_id} className="flex items-center justify-between gap-2 py-0.5">
                  <span className="truncate">{m.name || m.user_id}</span>
                  <button type="button" onClick={() => mute(m.user_id, m.name || 'member', !muted[m.user_id])} className={`flex items-center gap-1 rounded px-2 py-0.5 ${muted[m.user_id] ? 'bg-red-600' : 'bg-white/15 hover:bg-white/25'}`} data-testid={`call-mute-${m.user_id}`}>
                    {muted[m.user_id] ? <><Mic className="w-3 h-3" /> Unmute</> : <><MicOff className="w-3 h-3" /> Mute</>}
                  </button>
                  <button type="button" onClick={() => remove(m.user_id, m.name || 'member')} className="flex items-center gap-1 rounded px-2 py-0.5 bg-white/15 hover:bg-red-600" title="Remove from call" data-testid={`call-remove-${m.user_id}`}><UserMinus className="w-3 h-3" /> Remove</button>
                </div>
              ))}
            </div>
          )}
          {pending.length === 0 && others.length === 0 && <p className="text-gray-400">Everyone has joined.</p>}
        </div>
      )}
    </div>
  );
}
